import { BlackHoleRobotAI } from "../ai/blackHoleRobotAI";
import type { Board } from "../board";
import { DamageType } from "../damageType";
import { Direction, nearestDirectionFromVector, reverseDirection } from "../direction";
import { getMobGoldValue } from "../economy/mobGoldValues";
import type { MusicOverrider } from "../interfaces";
import { Mob } from "../mob";
import { MusicId } from "../musicId";
import { ParticleType } from "../particleType";
import type { PhysicalThing } from "../physicalThing";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { BlackHoleProjectile } from "../projectiles/blackHoleProjectile";
import { deserializeXY } from "../serialization";
import { MovementEffect } from "../effects/movementEffect";
import { ParticleEffect } from "../effects/particleEffect";
import { BaseKeyCardLootBox } from "../things/baseKeyCardLootBox";
import { ScrapMetal } from "../things/scrapMetal";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import type { Thing } from "../thing";
import { XY } from "../xy";

const FIRE_COOLDOWN_TURNS = 5;
const CHARGE_TURNS = 2;
const POST_FIRE_IMMOBILE_TURNS = 1;
const POINT_BLANK_RADIUS = 1;
export const MIN_FIRE_DISTANCE = 4;

export class BlackHoleRobot extends Mob implements MusicOverrider {
	static key = "BlackHoleRobot";

	private chargeTurnsRemaining: number = 0;
	private aimDirection: Direction | null = null;
	private ai: BlackHoleRobotAI;

	constructor(xy: XY) {
		super(xy);
		this.ai = new BlackHoleRobotAI(this);
		this.hp = STD_BASE_HP * 20;
		this.maxHp = STD_BASE_HP * 20;
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.damageMultipliers.set(DamageType.MELEE, 0.5);
		this.damageMultipliers.set(DamageType.ICE, 0);
		this.setAttackPeriod(FIRE_COOLDOWN_TURNS);
	}

	isCharging(): boolean {
		return this.chargeTurnsRemaining > 0;
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(BlackHoleRobot.key);
	}

	getMusicOverride(): MusicId | null {
		return MusicId.BOSS_BLACK_HOLE;
	}

	generateCorpse(): PhysicalThing | null {
		return new ScrapMetal(this.xy);
	}

	override generateDeathDrop(): PhysicalThing | null {
		return new BaseKeyCardLootBox(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.ROBOT_BIG_DIE);
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		const damage = super.applyDamage(type, amount, source);
		if (damage > 0 && this.hp === 0) {
			this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy, 0.6));
			this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.LARGE_ROBOT_DEBRIS, 4));
		}
		return damage;
	}

	processMobAction(board: Board): void {
		const player = board.player;
		if (!player || !player.active) return;
		if (this.isCharging()) {
			this.updateAim(player.xy);
			this.chargeTurnsRemaining--;
			if (this.chargeTurnsRemaining === 0) this.fire(board);
			return;
		}
		if (this.attackCadence.isReady() && this.hasShotWorthTaking(board, player.xy)) {
			this.chargeTurnsRemaining = CHARGE_TURNS;
			this.updateAim(player.xy);
			board.addNonPhysicalThing(new Sound(SoundId.ROBOT_TARGET_ACQUIRED, this.xy));
		} else {
			this.ai.processTurn();
		}
	}

	private hasShotWorthTaking(board: Board, playerXY: XY): boolean {
		if (this.isFireShadowed(board, playerXY)) return false;
		if (this.xy.isInSquareRadius(playerXY, POINT_BLANK_RADIUS)) return true;
		return this.xy.isAlignedWith(playerXY) && this.xy.distanceTo(playerXY) >= MIN_FIRE_DISTANCE;
	}

	private isFireShadowed(board: Board, playerXY: XY): boolean {
		const dir = nearestDirectionFromVector(playerXY.subtract(this.xy));
		if (dir === null) return false;
		return board
			.getAllThings()
			.some(
				(t) =>
					t instanceof BlackHoleProjectile &&
					t.active &&
					(t.direction === dir || t.direction === reverseDirection(dir)) &&
					t.shadows(playerXY),
			);
	}

	private updateAim(targetXY: XY): void {
		const delta = targetXY.subtract(this.xy);
		this.aimDirection = nearestDirectionFromVector(delta);
		if (delta.x > 0) this.setLookDirection(Direction.E);
		if (delta.x < 0) this.setLookDirection(Direction.W);
	}

	private fire(board: Board): void {
		if (this.aimDirection !== null) {
			const spawnXY = this.xy.addDirection(this.aimDirection);
			if (board.isInBounds(spawnXY)) {
				const projectile = new BlackHoleProjectile(spawnXY, this.aimDirection);
				board.addThing(projectile);
				board.addEffect(new MovementEffect(projectile, this.xy, spawnXY));
			}
		}
		this.attackCadence.consume();
		this.staggerMovement(POST_FIRE_IMMOBILE_TURNS);
		board.addNonPhysicalThing(new Sound(SoundId.SHOT_PLASMAGUN));
		this.aimDirection = null;
	}

	override getParticleType(): ParticleType | null {
		return this.isCharging() ? ParticleType.BLACK_HOLE_SUCTION : super.getParticleType();
	}

	override getParticleIntensity(): number {
		return this.isCharging() ? 1 : super.getParticleIntensity();
	}

	serialize(): Record<string, unknown> {
		const data = super.serialize();
		if (this.chargeTurnsRemaining !== 0) {
			data.chargeTurnsRemaining = this.chargeTurnsRemaining;
		}
		if (this.aimDirection !== null) {
			data.aimDirection = this.aimDirection;
		}
		data.ai = this.ai.serialize();
		return data;
	}

	static deserialize(data: any): BlackHoleRobot {
		const m = new BlackHoleRobot(deserializeXY(data.xy));
		m.restoreMobFields(data);
		m.chargeTurnsRemaining = data.chargeTurnsRemaining ?? 0;
		m.aimDirection = data.aimDirection ?? null;
		m.ai.restoreFrom(data.ai);
		return m;
	}
}
